/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 27/01/22
 */

import React, { PropsWithChildren, useCallback } from 'react';
import { createUnprovisionedContext } from '@/wpt-lib/utils/contextHelpers';
import { WorkplanSummary } from '@/wpt-lib/interfaces/Workplan';
import { useGetRequest } from '@/utils/useStatefulRequests';
import { useWorkplan } from '@/components/contexts/WorkplanContext/useWorkplan';
import { useAsyncLayoutEffect } from '@/utils/hooks/useAsyncEffect';
import useWorkplanVersion from '@/components/contexts/VersionContext/useWorkplanVersion';

interface IWorkplanSummaryContext {
    workplanSummary?: WorkplanSummary;
    error: unknown;
    loading: boolean;
    fetchSummary: () => Promise<void>;
}

export const WorkplanSummaryContext = createUnprovisionedContext<IWorkplanSummaryContext>('WorkplanSummaryContext');

export const WorkplanSummaryProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const { workplan, loading: workplanLoading } = useWorkplan();
    const { version } = useWorkplanVersion();

    const { makeRequest, loading, responseData, error } = useGetRequest<WorkplanSummary>();

    const fetchSummary = useCallback(async () => {
        if (workplan) {
            try {
                await makeRequest(`workplan/${workplan.id}/summary`, {
                    params: { version },
                });
            } catch (e) {
                // handled by hook
            }
        }
    }, [workplan, version]);

    useAsyncLayoutEffect(async () => {
        await fetchSummary();
    }, [workplan, version]);

    const context: IWorkplanSummaryContext = {
        workplanSummary: responseData,
        error: error,
        loading: workplanLoading || loading,
        fetchSummary,
    };

    return <WorkplanSummaryContext.Provider value={context}>{children}</WorkplanSummaryContext.Provider>;
};

export default WorkplanSummaryProvider;
